import { useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../api/client";
import { useMe } from "../api/me";
import {
  type MediaFolder,
  type MediaItem,
  type MediaStorage,
  useDeleteFolder,
  useDeleteMediaItem,
  useMedia,
} from "../api/media";
import { CREDITS, sayCredits } from "../copy/credits";
import { messageFor } from "../copy/errors";
import { MEDIA, say } from "../copy/outputs";
import { formatBytes, formatDate, formatRelative } from "../ui/format";
import { useNow } from "../ui/useNow";
import { UploadButton } from "../library/UploadButton";
import {
  Button,
  Card,
  ChevronDownIcon,
  PageHeader,
  Pill,
  Skeleton,
} from "../ui";
import type { PillTone } from "../ui";

/** The warning email gives seven days before anything is evicted
 *  (docs/02 §10). */
export const EVICTION_WARNING_DAYS = 7;

export function usagePercent(used: number, quota: number): number {
  if (quota <= 0) return 0;
  return Math.min(100, Math.round((used / quota) * 100));
}

/** What the used storage is made of, under the bar: media, the
 *  organisation's own objects, models uploaded and not yet added or
 *  discarded, and designs (§14.3's breakdown). Each part of Objects is named
 *  only when its feature is on, and leads to where its things can be
 *  deleted. On a phone each part is a line of its own. */
function Breakdown({ storage }: { storage: MediaStorage }) {
  const features = useMe().data?.features;
  if (!features?.assetlib) return null;
  const { assetlib_bytes, imports_bytes, studio_bytes } = storage;
  const rest =
    storage.used_bytes - assetlib_bytes - imports_bytes - studio_bytes;
  const link = "inline-flex min-h-[44px] items-center underline sm:min-h-0";
  return (
    <p
      className="m-0 flex flex-col text-sm text-slate-600 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2"
      data-testid="usage-breakdown"
    >
      <span>Media {formatBytes(Math.max(0, rest))}</span>
      <span aria-hidden="true" className="hidden sm:inline">
        ·
      </span>
      <Link to="/assets?tier=org" className={link}>
        Objects {formatBytes(assetlib_bytes)}
      </Link>
      {features.imports && imports_bytes > 0 ? (
        <>
          <span aria-hidden="true" className="hidden sm:inline">
            ·
          </span>
          <Link to="/assets/imports" className={link}>
            Models waiting {formatBytes(imports_bytes)}
          </Link>
        </>
      ) : null}
      {features.studio ? (
        <>
          <span aria-hidden="true" className="hidden sm:inline">
            ·
          </span>
          <Link to="/assets/studio" className={link}>
            Designs {formatBytes(studio_bytes)}
          </Link>
        </>
      ) : null}
    </p>
  );
}

/** The key of the folder of uploads tied to no ad. */
export const UPLOADS_FOLDER = "uploads";

/** A folder as the page shows it: an ad's (or an old job's), or Uploads. */
/** A folder as the page shows it: an ad's, or Uploads (no ad). */
export interface Folder extends Omit<MediaFolder, "ad_id"> {
  key: string;
  ad_id: string | null;
}

/** The server's folders (newest ad first) and, last, Uploads when it holds
 *  anything. */
export function foldersOf(media: {
  folders: MediaFolder[];
  uploads: MediaItem[];
}): Folder[] {
  const folders: Folder[] = media.folders.map((f) => ({
    ...f,
    key: f.ad_id,
  }));
  if (media.uploads.length > 0) {
    folders.push({
      key: UPLOADS_FOLDER,
      ad_id: null,
      title: MEDIA.uploads,
      created_at: "",
      bytes: media.uploads.reduce((n, i) => n + i.bytes, 0),
      items: media.uploads,
    });
  }
  return folders;
}

/** On a phone one folder is open at a time. */
const PHONE = "(max-width: 639px)";

function isPhone(): boolean {
  return (
    typeof window.matchMedia === "function" && window.matchMedia(PHONE).matches
  );
}

/** Which folders start open: the newest two ads, or on a phone the newest. */
export function openAtFirst(folders: Folder[], phone: boolean): Set<string> {
  return new Set(
    folders
      .filter((f) => f.key !== UPLOADS_FOLDER)
      .slice(0, phone ? 1 : 2)
      .map((f) => f.key),
  );
}

/** The pill of a file. */
export function kindLabel(item: MediaItem): string {
  return MEDIA.kinds[item.kind] ?? item.kind;
}

const KIND_TONE: Record<string, PillTone> = {
  rt: "purple",
  final: "purple",
  still: "info",
  upload: "neutral",
};

function FileRow({
  item,
  now,
  pending,
  onDelete,
}: {
  item: MediaItem;
  now: number;
  pending: string | null;
  onDelete: (item: MediaItem) => void;
}) {
  const video = item.mime?.startsWith("video/") === true;
  const picture = !video && (item.kind === "still" || item.kind === "upload");
  return (
    // On a phone: the pill and the name on one line, the size, the date and
    // the buttons on a second; from `sm` one row of five cells.
    <li
      className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-1 border-t border-slate-100 px-4 py-2 text-sm first:border-t-0 sm:grid-cols-[6.5rem_minmax(0,1fr)_5rem_7rem_auto] sm:gap-x-4"
      data-testid={`item-${item.id}`}
      aria-busy={pending === item.id}
    >
      <span>
        <Pill size="sm" tone={KIND_TONE[item.kind] ?? "neutral"}>
          {kindLabel(item)}
        </Pill>
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="flex min-w-0 items-center gap-2">
          {picture && item.thumb_url ? (
            <img
              src={item.thumb_url}
              alt=""
              className="h-8 w-8 shrink-0 rounded bg-slate-100 object-cover"
              loading="lazy"
            />
          ) : null}
          <span className="min-w-0 truncate font-semibold text-slate-800">
            {item.name}
          </span>
        </span>
        {video && item.url ? (
          <video
            controls
            preload="none"
            aria-label={say(MEDIA.videoPreview, { name: item.name })}
            src={item.url}
            className="mt-2 w-full max-w-xl rounded bg-slate-950"
          />
        ) : null}
      </span>
      <span className="col-start-2 flex flex-wrap items-center gap-3 sm:contents">
        <span className="text-slate-600 sm:text-right">
          {item.bytes ? formatBytes(item.bytes) : "—"}
        </span>
        <span className="text-slate-600">
          <time dateTime={item.created_at} title={item.created_at}>
            {formatRelative(item.created_at, now)}
          </time>
        </span>
        <span className="ml-auto flex items-center gap-1 sm:ml-0 sm:justify-end">
          {item.url ? (
            <Button as="a" href={item.url} size="sm" variant="ghost">
              {MEDIA.download}
            </Button>
          ) : null}
          {item.deletable ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={pending !== null}
              aria-busy={pending === item.id}
              aria-label={say(MEDIA.deleteFile, { name: item.name })}
              onClick={() => onDelete(item)}
            >
              {pending === item.id ? MEDIA.deleting : MEDIA.delete}
            </Button>
          ) : null}
        </span>
      </span>
    </li>
  );
}

/** One ad's folder (or Uploads): a header that opens and closes it, with
 *  the ad's page and **Delete ad**; inside, the files. */
function FolderCard({
  folder,
  open,
  onToggle,
  now,
  pending,
  onDeleteFile,
  onDeleteAd,
}: {
  folder: Folder;
  open: boolean;
  onToggle: () => void;
  now: number;
  pending: string | null;
  onDeleteFile: (item: MediaItem) => void;
  onDeleteAd: (folder: Folder) => void;
}) {
  const bodyId = `folder-${folder.key}`;
  const count = folder.items.length;
  const href = folder.ad_id ? `/ads/${encodeURIComponent(folder.ad_id)}` : null;
  return (
    <Card className="p-0" data-testid={`folder-${folder.key}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
        <h2 className="m-0 w-full min-w-0 text-base sm:w-auto sm:flex-1">
          <button
            type="button"
            className="flex min-h-[44px] w-full min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-left font-semibold text-slate-800 sm:min-h-0 sm:flex-nowrap"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={onToggle}
          >
            <ChevronDownIcon
              className={[
                "h-4 w-4 shrink-0 text-slate-500 transition-transform",
                open ? "" : "-rotate-90",
              ].join(" ")}
            />
            <span className="min-w-0 flex-1 break-words sm:flex-initial sm:truncate">
              {folder.title}
            </span>
            <span className="shrink-0 whitespace-nowrap text-sm font-normal text-slate-500">
              {count === 1 ? MEDIA.file : say(MEDIA.files, { count })} ·{" "}
              {formatBytes(folder.bytes)}
              {folder.ad_id && folder.credits ? (
                <span data-testid="folder-credits">
                  {" · "}
                  {sayCredits(CREDITS.folderTotal, {
                    credits: folder.credits,
                  })}
                </span>
              ) : null}
            </span>
          </button>
        </h2>
        {href ? (
          <span className="ml-6 flex items-center gap-1 sm:ml-0">
            <Button as={Link} to={href} size="sm" variant="ghost">
              {MEDIA.openAd}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={pending !== null}
              aria-busy={pending === folder.key}
              aria-label={`${MEDIA.deleteAd} ${folder.title}`}
              title={MEDIA.deleteAdTitle}
              onClick={() => onDeleteAd(folder)}
            >
              {pending === folder.key ? MEDIA.deleting : MEDIA.deleteAd}
            </Button>
          </span>
        ) : null}
      </div>
      {open ? (
        <ul
          id={bodyId}
          className="m-0 list-none border-t border-slate-200 p-0"
          aria-label={`Files of ${folder.title}`}
        >
          {folder.items.map((item) => (
            <FileRow
              key={`${item.kind}-${item.id}`}
              item={item}
              now={now}
              pending={pending}
              onDelete={onDeleteFile}
            />
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

/** `/media` (docs/SPEC.md §14.3, §25.6): the storage bar with its
 *  breakdown, then the organisation's files in folders by ad — each ad's
 *  real-time films, ray-traced finals, true renders and the uploads
 *  attached to it (no storyboard sheet: the scenes are the film's, played on
 *  the ad's page, P28-T10) — newest ad first, the newest two
 *  open, and a folder **Uploads** for files tied to no ad. Deleting a folder
 *  deletes the ad; a file can be deleted alone. The old jobs keep their
 *  folders until P25. */
export function MediaPage() {
  const media = useMedia();
  const deleteFolder = useDeleteFolder();
  const deleteItem = useDeleteMediaItem();
  const now = useNow(60_000);
  const [pending, setPending] = useState<string | null>(null);
  const [phone] = useState(isPhone);
  // Which folders are open: the newest two (one on a phone) until the user
  // chooses; a folder the user never touched follows that rule as data moves.
  const [chosen, setChosen] = useState<Map<string, boolean>>(new Map());
  const [uploadedInto, setUploadedInto] = useState<string | null>(null);

  const folders = media.data ? foldersOf(media.data) : [];
  const atFirst = openAtFirst(folders, phone);
  const isOpen = (key: string) =>
    chosen.get(key) ?? (uploadedInto === key || atFirst.has(key));

  const toggle = (key: string) => {
    const next = new Map(phone ? [] : chosen);
    if (phone) for (const f of folders) next.set(f.key, false);
    next.set(key, !isOpen(key));
    setChosen(next);
    setUploadedInto(null);
  };

  const onDeleteFile = (item: MediaItem) => {
    if (pending || !item.deletable) return;
    if (!window.confirm(say(MEDIA.confirmFile, { name: item.name }))) return;
    setPending(item.id);
    deleteItem.mutate(item.id, { onSettled: () => setPending(null) });
  };

  const onDeleteAd = (folder: Folder) => {
    const adId = folder.ad_id;
    if (pending || !adId) return;
    if (!window.confirm(MEDIA.confirmAd)) return;
    setPending(folder.key);
    deleteFolder.mutate({ ad_id: adId }, { onSettled: () => setPending(null) });
  };

  const failure = deleteFolder.error ?? deleteItem.error;

  return (
    <>
      <PageHeader
        eyebrow={MEDIA.eyebrow}
        title={MEDIA.title}
        meta={MEDIA.meta}
        right={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <UploadButton
              label={MEDIA.upload}
              variant="primary"
              onUploaded={() => {
                setUploadedInto(UPLOADS_FOLDER);
                if (phone) setChosen(new Map());
              }}
            />
          </div>
        }
      />
      {media.isPending ? (
        <Card aria-busy="true" className="flex flex-col gap-3 p-6">
          <Skeleton height={14} width="40%" />
          <Skeleton height={8} />
          <Skeleton height={14} width="80%" />
        </Card>
      ) : media.isError ? (
        <Card role="alert" className="p-6 text-slate-700">
          {messageFor(
            media.error instanceof ApiError ? media.error.code : undefined,
          )}
        </Card>
      ) : (
        <div className="flex flex-col gap-6">
          <Card className="flex flex-col gap-3 p-6">
            {(() => {
              const storage = media.data.storage;
              const { used_bytes: used, quota_bytes: quota } = storage;
              const percent = usagePercent(used, quota);
              return (
                <>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span
                      className="text-sm font-semibold text-slate-800"
                      data-testid="usage"
                    >
                      {formatBytes(used)} of {formatBytes(quota)} used (
                      {percent}
                      %)
                    </span>
                    <span className="text-xs text-slate-500">
                      Files are kept for at least {storage.guarantee_days} days
                      after they are created or last used, whatever the quota
                      says.
                    </span>
                  </div>
                  <div
                    role="progressbar"
                    aria-label="Storage used"
                    aria-valuemin={0}
                    aria-valuemax={quota}
                    aria-valuenow={Math.min(used, quota)}
                    className="h-2 w-full overflow-hidden rounded-pill bg-slate-200"
                  >
                    <div
                      className={[
                        "h-full rounded-pill transition-[width]",
                        percent >= 100 ? "bg-accent" : "bg-brand",
                      ].join(" ")}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <Breakdown storage={storage} />
                </>
              );
            })()}
          </Card>

          {media.data.storage.warned_at &&
          media.data.storage.eviction_enabled ? (
            <Card role="alert" className="p-4 text-slate-700">
              Your media storage has been over its quota since{" "}
              {formatDate(media.data.storage.warned_at)}. From{" "}
              {formatDate(
                new Date(
                  Date.parse(media.data.storage.warned_at) +
                    EVICTION_WARNING_DAYS * 86_400_000,
                ).toISOString(),
              )}{" "}
              the oldest files past their {media.data.storage.guarantee_days}
              -day guarantee will be removed unless you free some space.
            </Card>
          ) : null}

          {failure ? (
            <Card role="alert" className="p-4 text-slate-700">
              {messageFor(
                failure instanceof ApiError ? failure.code : undefined,
              )}
            </Card>
          ) : null}

          {folders.length === 0 ? (
            <Card className="flex flex-col items-start gap-3 p-8">
              <p className="m-0 text-lg font-semibold text-slate-800">
                {MEDIA.emptyTitle}
              </p>
              <p className="m-0 text-slate-600">{MEDIA.emptyBody}</p>
              <div className="flex flex-wrap items-center gap-2">
                <UploadButton
                  label={MEDIA.emptyUpload}
                  variant="primary"
                  testId="upload-input-empty"
                  onUploaded={() => setUploadedInto(UPLOADS_FOLDER)}
                />
                <Button as={Link} to="/new" variant="ghost">
                  {MEDIA.makeAd}
                </Button>
              </div>
            </Card>
          ) : (
            <div className="flex flex-col gap-3" data-testid="folders">
              {folders.map((folder) => (
                <FolderCard
                  key={folder.key}
                  folder={folder}
                  open={isOpen(folder.key)}
                  onToggle={() => toggle(folder.key)}
                  now={now}
                  pending={pending}
                  onDeleteFile={onDeleteFile}
                  onDeleteAd={onDeleteAd}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
